import styled, { keyframes, css } from 'styled-components';
import { theme } from '@/styles/theme';
import { Container, Section } from '@/components/ui/Container';
import { Card } from '@/components/ui/Card';
import { FadeInWhenVisible } from '@/components/animations/FadeInWhenVisible';
import { Music, Drum, Baby, Sparkles } from 'lucide-react';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { client } from '@/api/client';

// Images
import harmoniePhoto from '@/assets/images/Foto Harmonie.jpg';
import trommelkorpsPhoto from '@/assets/images/trommelkorps.webp';
import preciliaPhoto from '@/assets/images/precilia.webp';
import popciliaPhoto from '@/assets/images/popcilia.webp';

const PageHeader = styled(Section)`
  background: ${theme.colors.primary.navy};
  color: ${theme.colors.neutral.white};
  text-align: center;
  padding: ${theme.spacing[24]} 0 ${theme.spacing[16]};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[16]} 0 ${theme.spacing[12]};
  }
`;

const PageTitle = styled.h1`
  color: ${theme.colors.neutral.white};
  margin-bottom: ${theme.spacing[4]};
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.primary.lightGold};
  max-width: 700px;
  margin: 0 auto;
`;

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(212, 175, 55, 0);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0);
    transform: scale(1);
  }
`;

const GroupSection = styled.div`
  margin-bottom: ${theme.spacing[20]};
  scroll-margin-top: 100px; /* Offset for fixed header if any, or just spacing */

  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupCard = styled(Card)<{ $isHighlighted?: boolean }>`
  padding: 0;
  background: ${theme.colors.neutral.white};
  overflow: hidden;
  transition: all 0.5s ease-in-out;
  border: 2px solid transparent; /* Prevent layout shift */
  
  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      animation: ${pulseAnimation} 2s ease-in-out;
      border-color: ${theme.colors.primary.gold};
    `}
`;

const GroupContent = styled.div<{ $reverse?: boolean }>`
  display: flex;
  flex-direction: ${({ $reverse }) => ($reverse ? 'row-reverse' : 'row')};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  min-height: 400px;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    min-height: 300px;
    position: relative;
    
    img {
      position: relative;
    }
  }
`;

const TextContainer = styled.div`
  flex: 1;
  padding: ${theme.spacing[12]};
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[8]};
  }
`;

const GroupTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  color: ${theme.colors.primary.navy};
  font-size: ${theme.fontSizes['3xl']};
  margin-bottom: ${theme.spacing[6]};
`;

const GroupDescription = styled.div`
  color: ${theme.colors.neutral.gray};
  line-height: ${theme.lineHeights.relaxed};
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing[8]};

  p {
    margin-bottom: ${theme.spacing[4]};
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StatBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  background: ${theme.colors.secondary.ivory};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.xl};
  align-self: flex-start;
  border-left: 4px solid ${theme.colors.primary.gold};
`;

const StatNumber = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary.navy};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.neutral.gray};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${theme.fontWeights.semibold};
`;

const IconWrapper = styled.div`
  color: ${theme.colors.primary.gold};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const GroupsPage = () => {
  const location = useLocation();
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [counts, setCounts] = useState({
    harmonie: 90,
    trommelkorps: 40,
    precilia: 30,
    popcilia: 20
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await client.get('/settings');
        if (res.data) {
          const { count_harmonie, count_trommelkorps, count_precilia, count_popcilia } = res.data;
          
          const roundDown = (val: string) => Math.floor((parseInt(val) || 0) / 10) * 10;
          
          setCounts({
            harmonie: roundDown(count_harmonie) || 90,
            trommelkorps: roundDown(count_trommelkorps) || 40,
            precilia: roundDown(count_precilia) || 30,
            popcilia: roundDown(count_popcilia) || 20
          });
        }
      } catch (error) {
        console.error('Failed to fetch group counts', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      
      if (element) {
        // Add a small delay to ensure layout is ready
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedId(id);
          
          // Remove highlight after animation duration (2s)
          setTimeout(() => {
            setHighlightedId(null);
          }, 2000);
        }, 100);
      }
    }
  }, [location]);

  return (
    <>
      <PageHeader>
        <Container>
          <FadeInWhenVisible>
            <PageTitle>Onze Groepen</PageTitle>
            <PageSubtitle>
              Vier unieke ensembles, één gedeelde passie voor muziek
            </PageSubtitle>
          </FadeInWhenVisible>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          <GroupSection id="harmonie">
            <FadeInWhenVisible>
              <GroupCard elevated $isHighlighted={highlightedId === 'harmonie'}>
                <GroupContent>
                  <ImageContainer>
                    <img src={harmoniePhoto} alt="Koninklijke Poperingse Harmonie Sint-Cecilia" />
                  </ImageContainer>
                  <TextContainer>
                    <GroupTitle>
                      <IconWrapper>
                        <Music size={40} />
                      </IconWrapper>
                      De Harmonie
                    </GroupTitle>
                    <GroupDescription>
                      <p>
                        De Koninklijke Poperingse Harmonie is ons hoofdorkest, bestaande uit ervaren
                        muzikanten die een breed en gevarieerd repertoire uitvoeren. Van klassieke
                        composities tot moderne arrangementen, de harmonie brengt muziek van hoogstaand
                        niveau.
                      </p>
                      <p>
                        Als vlaggenschip van onze vereniging verzorgen we concerten, nemen we deel aan
                        wedstrijden en luisteren we officiële plechtigheden op in en rond Poperinge.
                      </p>
                    </GroupDescription>
                    <StatBox>
                      <StatNumber>
                        <AnimatedCounter key={counts.harmonie} end={counts.harmonie} suffix="+" />
                      </StatNumber>
                      <StatLabel>
                        Muzikanten in<br />de Harmonie
                      </StatLabel>
                    </StatBox>
                  </TextContainer>
                </GroupContent>
              </GroupCard>
            </FadeInWhenVisible>
          </GroupSection>

          <GroupSection id="trommelkorps">
            <FadeInWhenVisible>
              <GroupCard elevated $isHighlighted={highlightedId === 'trommelkorps'}>
                <GroupContent $reverse>
                  <ImageContainer>
                    <img src={trommelkorpsPhoto} alt="KPH Trommelkorps" />
                  </ImageContainer>
                  <TextContainer>
                    <GroupTitle>
                      <IconWrapper>
                        <Drum size={40} />
                      </IconWrapper>
                      Trommelkorps
                    </GroupTitle>
                    <GroupDescription>
                      <p>
                        Het Trommelkorps is ons slagwerkensemble dat zorgt voor ritme en
                        energie. Met strakke choreografieën en krachtige ritmes vormen ze een
                        onmisbaar en spectaculair onderdeel van onze optredens.
                      </p>
                      <p>
                        Naast het begeleiden van de harmonie tijdens uitstappen, treden ze ook
                        regelmatig zelfstandig op met indrukwekkende percussieshows.
                      </p>
                    </GroupDescription>
                    <StatBox>
                      <StatNumber>
                        <AnimatedCounter key={counts.trommelkorps} end={counts.trommelkorps} suffix="+" />
                      </StatNumber>
                      <StatLabel>
                        Leden in het<br />Trommelkorps
                      </StatLabel>
                    </StatBox>
                  </TextContainer>
                </GroupContent>
              </GroupCard>
            </FadeInWhenVisible>
          </GroupSection>

          <GroupSection id="precilia">
            <FadeInWhenVisible>
              <GroupCard elevated $isHighlighted={highlightedId === 'precilia'}>
                <GroupContent>
                  <ImageContainer>
                    <img src={preciliaPhoto} alt="Précilia Jeugdharmonie" />
                  </ImageContainer>
                  <TextContainer>
                    <GroupTitle>
                      <IconWrapper>
                        <Sparkles size={40} />
                      </IconWrapper>
                      Précilia
                    </GroupTitle>
                    <GroupDescription>
                      <p>
                        Précilia is onze bloeiende jeugdharmonie waar jonge muzikanten hun talenten
                        verder kunnen ontwikkelen. Het is de perfecte tussenstap tussen de muziekschool
                        en het grote orkest.
                      </p>
                      <p>
                        Hier leren jongeren niet alleen samenspelen onder leiding van een dirigent,
                        maar smeden ze ook vriendschappen voor het leven tijdens repetities en
                        leuke nevenactiviteiten.
                      </p>
                    </GroupDescription>
                    <StatBox>
                      <StatNumber>
                        <AnimatedCounter key={counts.precilia} end={counts.precilia} suffix="+" />
                      </StatNumber>
                      <StatLabel>
                        Enthousiastelingen<br />in Précilia
                      </StatLabel>
                    </StatBox>
                  </TextContainer>
                </GroupContent>
              </GroupCard>
            </FadeInWhenVisible>
          </GroupSection>

          <GroupSection id="popcilia">
            <FadeInWhenVisible>
              <GroupCard elevated $isHighlighted={highlightedId === 'popcilia'}>
                <GroupContent $reverse>
                  <ImageContainer>
                    <img src={popciliaPhoto} alt="Popcilia Instaporkest" />
                  </ImageContainer>
                  <TextContainer>
                    <GroupTitle>
                      <IconWrapper>
                        <Baby size={40} />
                      </IconWrapper>
                      Popcilia
                    </GroupTitle>
                    <GroupDescription>
                      <p>
                        Popcilia is ons instaporkest voor de allerjongste muzikantjes. Vanaf het moment
                        dat kinderen hun instrument leren bespelen, zijn ze welkom om hier hun eerste
                        orkestervaring op te doen.
                      </p>
                      <p>
                        Op een speelse en laagdrempelige manier maken ze kennis met samen musiceren,
                        waarbij het plezier in muziek centraal staat.
                      </p>
                    </GroupDescription>
                    <StatBox>
                      <StatNumber>
                        <AnimatedCounter key={counts.popcilia} end={counts.popcilia} suffix="+" />
                      </StatNumber>
                      <StatLabel>
                        Starters in<br />Popcilia
                      </StatLabel>
                    </StatBox>
                  </TextContainer>
                </GroupContent>
              </GroupCard>
            </FadeInWhenVisible>
          </GroupSection>
        </Container>
      </Section>
    </>
  );
};